import {create} from 'zustand';
import {devtools, persist} from 'zustand/middleware';
import {Banner} from '@lightricks/react-design-system';
import {Step, STEPS, DEFAULT_STEP_ORDER} from './onboardingSteps';
import {type Specialty, type Capability, type Locale} from '@/schema/User';
import type {PortfolioMediaItem} from '@/types/portfolioMediaItem';

export type Segment = 'creator' | 'influencer';

export type GenderOption = 'Female' | 'Male' | 'Other' | 'Prefer not to say';

const FLOW_NAME_VALUES = ['onboarding', 'reonboarding', undefined] as const;
export type FlowName = (typeof FLOW_NAME_VALUES)[number];

const ONBOARDING_STORAGE_KEY = 'onboarding-storage';
export const ONBOARDING_STATE_EXPIRATION_TIME_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export type OnboardingState = {
  step: Step;
  previousStep: Step | undefined;
  isCurrentStepValid: boolean;
  banner: Pick<
    React.ComponentProps<typeof Banner>,
    'isOpen' | 'title' | 'children' | 'severity'
  >;
  firstName: string;
  lastName: string;
  birthdate: Date | undefined;
  segment: Segment | undefined;
  location: string | undefined;
  locale: Locale | undefined;
  gender: GenderOption | undefined; // this transitions from undefined to null if the user selects "prefer not to say"
  bio: string | undefined;
  minCollaborationRate: number | undefined;
  specialties: Set<Specialty>;
  capabilities: Set<Capability>;
  portfolioMediaItems: PortfolioMediaItem[];
  portfolioLink: string | undefined;
  profilePictureUrl: string | undefined;
  platformError?: string;
  isHydrated: boolean;
  stepOrder: Step[];
  isStepOrderPersisted: boolean;
  isStepUploading: boolean;
  analyticsFlowId: string | undefined;
  flowName: FlowName;
  userCreatedAt: number | undefined;
};

export type OnboardingActions = {
  setStep: (step: Step) => void;
  setStepValidity: (isValid: boolean) => void;
  openBanner: (banner: Omit<OnboardingState['banner'], 'isOpen'>) => void;
  closeBanner: () => void;
  hydrate: (state: Partial<OnboardingState>) => void;
  setFirstName: (firstName: string) => void;
  setLastName: (lastName: string) => void;
  setBirthdate: (birthdate: Date) => void;
  setSegment: (segment?: Segment) => void;
  setLocation: (location: string | undefined) => void;
  setLocale: (locale: Locale | undefined) => void;
  setGender: (gender?: GenderOption) => void;
  setBio: (bio: string) => void;
  setMinCollaborationRate: (rate: number) => void;
  setSpecialties: (specialties: Set<Specialty>) => void;
  setCapabilities: (capabilities: Set<Capability>) => void;
  setPortfolioMediaItems: (mediaItems: PortfolioMediaItem[]) => void;
  setPortfolioLink: (link: string) => void;
  setProfilePictureUrl: (url: string) => void;
  setPlatformError: (error: string | undefined) => void;
  setStepOrder: (stepOrder: Step[]) => void;
  setStepUploading: (isStepUploading: boolean) => void;
  setAnalyticsFlowId: (analyticsFlowId: string) => void;
  setFlowName: (flowName: FlowName) => void;
  clearPersistentData: () => void;
};

const DEFAULT_ONBOARDING_STATE: OnboardingState = {
  step: STEPS.NAME_AND_BIRTHDATE,
  previousStep: undefined,
  banner: {isOpen: false, title: null, children: null, severity: 'info'},
  isCurrentStepValid: false,
  firstName: '',
  lastName: '',
  birthdate: undefined,
  segment: undefined,
  location: undefined,
  locale: undefined,
  gender: undefined,
  bio: undefined,
  specialties: new Set(),
  capabilities: new Set(),
  minCollaborationRate: 0,
  portfolioMediaItems: [],
  portfolioLink: undefined,
  profilePictureUrl: undefined,
  platformError: undefined,
  isHydrated: false,
  stepOrder: DEFAULT_STEP_ORDER,
  isStepOrderPersisted: false,
  isStepUploading: false,
  analyticsFlowId: undefined,
  flowName: undefined,
  userCreatedAt: undefined,
};

type State = OnboardingState & OnboardingActions;

type PersistedState = {
  stepOrder: Step[];
  step: Step;
  previousStep: Step | undefined;
  analyticsFlowId: string | undefined;
  flowName: OnboardingState['flowName'];
  createdAt: number;
};

const isValidPersistedState = (state: unknown): state is PersistedState => {
  if (!state || typeof state !== 'object') return false;

  const maybeValid = state as Partial<PersistedState>;

  if (
    maybeValid.stepOrder &&
    !maybeValid.stepOrder.every((step: Step) =>
      Object.values(STEPS).includes(step)
    )
  ) {
    return false;
  }

  if (maybeValid.step && !Object.values(STEPS).includes(maybeValid.step)) {
    return false;
  }

  if (
    maybeValid.previousStep &&
    !Object.values(STEPS).includes(maybeValid.previousStep)
  ) {
    return false;
  }

  if (
    maybeValid.analyticsFlowId &&
    typeof maybeValid.analyticsFlowId !== 'string'
  ) {
    return false;
  }

  if (
    maybeValid.flowName !== undefined &&
    typeof maybeValid.flowName !== 'string' &&
    !FLOW_NAME_VALUES.includes(maybeValid.flowName)
  ) {
    return false;
  }

  if (maybeValid.createdAt && typeof maybeValid.createdAt !== 'number') {
    return false;
  }

  return true;
};

const isStateExpired = (createdAt: number | undefined): boolean => {
  if (!createdAt) return true;

  const currentTime = Date.now();
  return currentTime - createdAt > ONBOARDING_STATE_EXPIRATION_TIME_MS;
};

export const useOnboardingStore = create<State>()(
  devtools(
    persist(
      (set) => ({
        ...DEFAULT_ONBOARDING_STATE,
        setStep: (step) => {
          set((state) => ({previousStep: state.step, step}));
        },
        setStepValidity: (isValid) => set({isCurrentStepValid: isValid}),
        openBanner: (banner) => set({banner: {...banner, isOpen: true}}),
        closeBanner: () => set({banner: {...DEFAULT_ONBOARDING_STATE.banner}}),
        hydrate: (state) => set({...state, isHydrated: true}),
        setFirstName: (firstName) => set({firstName}),
        setLastName: (lastName) => set({lastName}),
        setBirthdate: (birthdate) => set({birthdate}),
        setSegment: (segment) => set({segment}),
        setLocation: (location) => set({location}),
        setLocale: (locale) => set({locale}),
        setGender: (gender) => set({gender}),
        setBio: (bio) => set({bio}),
        setMinCollaborationRate: (minCollaborationRate) =>
          set({minCollaborationRate}),
        setSpecialties: (specialties: Set<Specialty>) => set({specialties}),
        setCapabilities: (capabilities: Set<Capability>) => set({capabilities}),
        setPortfolioMediaItems: (newMediaItems: PortfolioMediaItem[]) =>
          set((state) => ({
            portfolioMediaItems: [
              ...state.portfolioMediaItems,
              ...newMediaItems,
            ],
          })),
        setPortfolioLink: (link: string) => set({portfolioLink: link}),
        setProfilePictureUrl: (url: string) => set({profilePictureUrl: url}),
        setPlatformError: (platformError) => set({platformError}),
        setStepOrder: (stepOrder: Step[]) => set({stepOrder}),
        setStepUploading: (isStepUploading: boolean) => set({isStepUploading}),
        setAnalyticsFlowId: (analyticsFlowId: string) => set({analyticsFlowId}),
        setFlowName: (flowName: OnboardingState['flowName']) => set({flowName}),
        clearPersistentData: () => {
          localStorage.removeItem(ONBOARDING_STORAGE_KEY);
        },
      }),
      {
        name: ONBOARDING_STORAGE_KEY,
        version: 1,
        partialize: (state: State): PersistedState => ({
          stepOrder: state.stepOrder,
          step: state.step,
          previousStep: state.previousStep,
          analyticsFlowId: state.analyticsFlowId,
          flowName: state.flowName,
          createdAt: Date.now(),
        }),
        migrate: (persistedState: unknown, version: number) => {
          if (version === 2) {
            // Example: migrate from version 1 to version 2
            // persistedState = migrateV1ToV2(persistedState);
          }

          const defaultState = {
            stepOrder: DEFAULT_STEP_ORDER,
            step: DEFAULT_ONBOARDING_STATE.step,
            previousStep: DEFAULT_ONBOARDING_STATE.previousStep,
            analyticsFlowId: DEFAULT_ONBOARDING_STATE.analyticsFlowId,
            flowName: DEFAULT_ONBOARDING_STATE.flowName,
            createdAt: Date.now(),
          };

          // Validate the data
          if (!isValidPersistedState(persistedState)) {
            console.warn(
              'Invalid persisted state found in localStorage, using defaults'
            );
            return defaultState;
          }

          return persistedState;
        },
        merge: (persistedState: unknown, defaultState: State) => {
          // At this point, persistedState has been validated by migrate
          const validState = persistedState as PersistedState;
          if (isStateExpired(validState.createdAt)) {
            return defaultState;
          }
          return {
            ...defaultState,
            stepOrder: validState.stepOrder || DEFAULT_STEP_ORDER,
            step: validState.step || DEFAULT_ONBOARDING_STATE.step,
            previousStep:
              validState.previousStep || DEFAULT_ONBOARDING_STATE.previousStep,
            isStepOrderPersisted: Boolean(validState.stepOrder?.length),
            analyticsFlowId:
              validState.analyticsFlowId ||
              DEFAULT_ONBOARDING_STATE.analyticsFlowId,
            flowName: validState.flowName,
          };
        },
      }
    )
  )
);
